import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useFormikContext } from "formik";
import React, { FC, useState } from 'react';
import { toast } from "react-toastify";
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { executeCommand } from '../../services/executeCommandService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { validateItemName, validatePassword } from "../../utilities/Validation";
import FullScreenActivityFrame from '../activity-frame/full-screen-activity-spinner';
import { useDialog } from "../dialog/DialogContext";
import Label from "../label/label";
import Togglebox from "../togglebox/togglebox";

interface VingcardAccountCardProps {
    isLicensed: boolean,
    currentItem: any,
    isAccountPopulated: boolean, // Whether all the required details are populated to allow querying
}

const VingcardAccountCard: FC<VingcardAccountCardProps> = ({ isLicensed, currentItem, isAccountPopulated }) => {
    const { errors, values, setFieldValue, dirty } = useFormikContext<any>();
    const { translate } = useLocale();
    const [showPassword, setShowPassword] = useState(false);
    const dialog = useDialog();
    const [busy, setBusy] = useState(false);

    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }, errors as any);
        if (error) {
            return <span className='text-red-600 text-xs -ml-3 pr-8'>{error}</span>
        } else {
            return null;
        }
    };

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * @returns 
    */
    const canTestAccount = () => {
        return isLicensed && isAccountPopulated && !dirty;
    }

    const onEnabled = () => {
        const newEnabledState = !values.enabled;

        if (newEnabledState && !isLicensed) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
            setFieldValue("enabled", false);
            return;
        }

        setFieldValue("enabled", newEnabledState);
    }

    const testAccount = () => {

        if (dirty) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unsavedChanges), message: translate.byKey(TranslationKeys.saveChangesBeforeTesting) });
            return;
        }

        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                toast.success(translate.byKey(TranslationKeys.testingSettings));
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onValidatePassword = (value: string) => {

        // Ignores validation only if password is already captured and no new edits are made
        if (currentItem?.isPasswordCaptured && !value) {
            return null;
        }

        // If this is either the initial capture of the password or an edit, validate it
        return validatePassword(value, translate, currentItem, 2);
    }

    return <div className="w-full p-2 inline-flex justify-start items-top bg-white">
        {busy ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.pleaseWaitWhileWeTestTheAccount)} /> : null}
        <div className="flex flex-col py-4 px-8">

            {/* URL */}
            <div className="mb-1">
                <div className="flex flex-row justify-between" id="urlLabel">
                    <Label htmlFor="details.url.value" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.url)}:</Label>
                    {errorHandler("details.url.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='urlInputFieldId'
                        name="details.url.value"
                        type='text'
                        placeholder="https://<server-ip>:443/api/v1"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-56 bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>

            {/* Username */}
            <div className="mb-1">
                <div className="flex flex-row justify-between" id="usernameLabel">
                    <Label htmlFor="details.username.value" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.username)}:</Label>
                    {errorHandler("details.username.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='usernameInputFieldId'
                        name="details.username.value"
                        type='text'
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-56 bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>

            {/* Password */}
            <div>
                <div className="flex flex-row justify-between mb-1" id="passwordLabel">
                    <Label
                        htmlFor="tempPassword"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.password)}:</Label>
                    {errorHandler("tempPassword")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='passwordInputFieldId'
                        type={showPassword ? 'text' : 'password'}
                        name="tempPassword"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-56 bg-gray-input"
                        validate={(value) => onValidatePassword(value)} 
                        placeholder={(currentItem?.isPasswordCaptured) ? "********" : translate.byKey(TranslationKeys.enterNewPassword)}/>
                    <div className="flex relative w-6 ml-1">
                        <FontAwesomeIcon
                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                            onClick={() => setShowPassword(showPassword => !showPassword)}
                            icon={showPassword ? faEye : faEyeSlash} />
                    </div>
                </div>
            </div>
        </div>

        {/* Right-hand column */}
        <div className="flex flex-col py-4 px-8">

            {/* Enabled */}
            <div className="mb-1">
                <div className="flex flex-row justify-between" id="enabled">
                    <Label htmlFor="enabled" className="text-xs text-gray-600 mt-1">
                        {translate.byKey(TranslationKeys.enabled)}:
                    </Label>
                </div>
                <Togglebox name="enabled" onChanged={onEnabled} label={""} />
            </div>

         {/* Test the account is setup correctly */}
         <div className="flex w-full py-4">
            <button
                type='button'
                    className={!canTestAccount()
                    ? " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2  mt-3"
                    : " h-8 px-2 mt-1 secondary bg-sidemenu-focus text-white shadow-lg"}
                    disabled={!canTestAccount()}
                onClick={(e) => testAccount()}
                id="testEmailSettingsButton">
                <span className="mr-2">{translate.byKey(TranslationKeys.testAccount)}</span>
            </button>
        </div>
        </div>
         
    </div>
}

export default VingcardAccountCard;
